import { Row, Col, Card, Typography, Divider, Button, Statistic, Space, Badge } from "antd";
import PageWrapper from "../../utils/PageWrapper";
import { useEffect, useState } from "react";
import TimeRangeSelector from "./TimeRangeSelector";
import DonaProgreso from "./DonaProgreso";
import AreaCostos from "./AreaCostos";
import LineChart from "./LineChart";
import BarChart from "./BarChart";
import axios from "axios";
import { useAuth } from "../../layout/auth";
import moment from "moment";
import ListDrawer, { getStatusTag } from "./ListDrawer";
import PageTitle from "../../utils/PageTitle";
import { BarChart2, FileText, DollarSign, CheckCircle, Clock, PieChart, TrendingUp, Eye } from "lucide-react";

const { Title, Text } = Typography;

export default function Stats() {
  // State declarations at the top
  const [countData, setCountData] = useState();
  const [montoData, setMontoData] = useState();
  const [montoTotalTotal, setMontoTotalTotal] = useState();
  const [montoTotalAnual, setMontoTotalAnual] = useState();
  const [montoTotalMensual, setMontoTotalMensual] = useState();
  const [montoTotalPeriodo, setMontoTotalPeriodo] = useState();
  const [statusCounts, setStatusCounts] = useState();
  const [totalCount, setTotalCount] = useState();
  const [sinPapeleria, setSinPapeleria] = useState();
  const [sinPago, setSinPago] = useState();
  const [totalMonto, setTotalMonto] = useState();
  const [totalMontoUnidad, setTotalMontoUnidad] = useState();
  const [totalMontoVerificador, setTotalMontoVerificador] = useState();
  const [costData, setCostData] = useState();
  const [startDate, setStartDate] = useState(moment().startOf("quarter").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().endOf("quarter").format("YYYY-MM-DD"));
  const [groupBy, setGroupBy] = useState("weekly");
  const [selectedRange, setSelectedRange] = useState("thisQuarter");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerData, setDrawerData] = useState([]);
  const [drawerTitle, setDrawerTitle] = useState("");
  const [listData, setListData] = useState([]);
  const [drawerColumns, setDrawerColumns] = useState([]);

  const auth = useAuth();

  // Helper function to get API parameters based on user role
  const getApiParams = (currentStartDate, currentEndDate, currentGroupBy) => {
    const params = {
      startDate: currentStartDate,
      endDate: currentEndDate,
      rol: auth.Rol,
    };
    // If not director or administrator, filter by user ID
    if (auth.Rol !== "director" && auth.Rol !== "administrador") {
      params.u = auth.UID;
    }
    if (currentGroupBy) {
      params.groupBy = currentGroupBy;
    }
    return params;
  };

  const fetchCount = async (endpoint, params, setter) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/${endpoint}`, { params });
      setter(res.data[0]);
    } catch (err) {
      console.error(`Error fetching ${endpoint}:`, err);
    }
  };

  useEffect(() => {
    if (!auth?.UID) return;

    const fetchData = async () => {
      const commonParams = getApiParams(startDate, endDate);

      await fetchCount("totalcount", commonParams, setTotalCount);
      if (auth.Rol !== "administrador") {
        await fetchCount("countsinpapeleria", commonParams, setSinPapeleria);
      }
      await fetchCount("countsinpago", commonParams, setSinPago);
      await fetchCount("totalsum", commonParams, setTotalMonto);
      await fetchCount("totalsumunidad", commonParams, setTotalMontoUnidad);
      await fetchCount("totalsumverificador", commonParams, setTotalMontoVerificador);
    };

    fetchData();
  }, [auth?.UID, auth?.Rol, startDate, endDate]);

  const fetchStatusCounts = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/count`, {
        params: getApiParams(startDate, endDate),
      });
      setStatusCounts(res.data[0]);
    } catch (err) {
      console.error("Error fetching status counts:", err);
    }
  };

  const fetchCostData = async () => {
    console.log("Fetching cost data with params:", { u: auth?.UID, startDate, endDate, groupBy });
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/monto`, {
        params: getApiParams(startDate, endDate, groupBy),
      });
      setCostData(res.data);
    } catch (err) {
      console.error("Error fetching cost data:", err);
    }
  };

  useEffect(() => {
    if (!auth?.UID) return;

    fetchStatusCounts();
    fetchCostData();
  }, [auth?.UID, startDate, endDate, groupBy]);

  const handleTimeRangeUpdate = (start, end, group, range) => {
    setStartDate(start);
    setEndDate(end);
    setGroupBy(group);
    setSelectedRange(range);
  };

  const statusLabels = ["Iniciadas", "Por emitir", "Emitidas", "Completas", "Canceladas"];
  const colors = ["#dee5e9", "#78beff", "#faad14", "#52c41a", "#ff4d4f"];

  const fetchUserStats = async () => {
    if (!auth?.UID) return;

    try {
      const [countRes, montoRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/countByUser`, {
          params: getApiParams(startDate, endDate, groupBy),
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/montoByUser`, {
          params: getApiParams(startDate, endDate, groupBy),
        }),
      ]);

      setCountData(formatChartDataCount(countRes.data));
      setMontoData(formatChartDataMontos(montoRes.data));
    } catch (err) {
      console.error("Error fetching user stats:", err);
    }
  };

  useEffect(() => {
    fetchUserStats();
  }, [auth?.UID, startDate, endDate, groupBy]);

  const fetchAggregateStats = async () => {
    if (!auth?.UID || (auth?.Rol !== "director" && auth?.Rol !== "administrador")) return;

    try {
      const startOfYear = moment().startOf("year").format("YYYY-MM-DD");
      const endOfYear = moment().endOf("year").format("YYYY-MM-DD");
      const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
      const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");

      const [totalRes, anualRes, mensualRes, periodoRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/montoTotalByUser`, {
          params: { rol: auth.Rol },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/montoTotalByUser`, {
          params: { rol: auth.Rol, startDate: startOfYear, endDate: endOfYear },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/montoTotalByUser`, {
          params: { rol: auth.Rol, startDate: startOfMonth, endDate: endOfMonth },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/montoTotalByUser`, {
          params: { rol: auth.Rol, startDate, endDate },
        }),
      ]);

      const processData = (data) => ({
        labels: data.map((i) => i.usuario_nombre),
        rawData: data,
        datasets: [
          {
            label: "Verificador",
            data: data.map((i) => i.total_monto * 0.6),
            backgroundColor: "#1890ff",
            stack: "stack0",
          },
          {
            label: "Empresa",
            data: data.map((i) => i.total_monto * 0.4),
            backgroundColor: "#faad14",
            stack: "stack0",
          },
        ],
      });

      setMontoTotalTotal(processData(totalRes.data));
      setMontoTotalAnual(processData(anualRes.data));
      setMontoTotalMensual(processData(mensualRes.data));
      setMontoTotalPeriodo(processData(periodoRes.data));
    } catch (err) {
      console.error("Error fetching aggregate user stats:", err);
    }
  };

  useEffect(() => {
    fetchAggregateStats();
  }, [auth?.UID, auth?.Rol, startDate, endDate]);

  const formatChartDataMontos = (data) => {
    const labels = [
      ...new Set(data.map((item) => (typeof item.fecha === "string" ? item.fecha.split("T")[0] : String(item.fecha)))),
    ].sort();

    const userGroups = {};

    data.forEach(({ fecha, UsuarioID, total_monto, usuario_nombre }) => {
      const formattedFecha = typeof fecha === "string" ? fecha.split("T")[0] : String(fecha);

      const colorIndex = UsuarioID % 12;
      const colorPalette = [
        "#ff4d4d",
        "#18dcff",
        "#cd84f1",
        "#ff4757",
        "#b33939",
        "#ff793f",
        "#7158e2",
        "#706fd3",
        "#ffaf40",
        "#3ae374",
        "#ffcccc",
        "#fff200",
      ];

      const userColor = colorPalette[colorIndex];

      if (!userGroups[UsuarioID]) {
        userGroups[UsuarioID] = {
          label: usuario_nombre ? `${usuario_nombre}` : `User ${UsuarioID}`,
          data: Array(labels.length).fill(0),
          borderWidth: 2,
          borderColor: userColor,
        };
      }
      const index = labels.indexOf(formattedFecha);
      if (index !== -1) userGroups[UsuarioID].data[index] = total_monto ?? 0;
    });

    const datasets = Object.values(userGroups);
    datasets.forEach((ds, index) => {
      if (index > 0) ds.hidden = true;
    });

    return { labels, datasets };
  };

  const formatChartDataCount = (data) => {
    const labels = [
      ...new Set(
        data.map((item) => {
          if (typeof item.fecha === "string") {
            return item.fecha.split("T")[0];
          } else if (typeof item.fecha === "number") {
            return moment()
              .week(item.fecha % 100)
              .year(Math.floor(item.fecha / 100))
              .format("YYYY-Www");
          }
          return item.fecha;
        })
      ),
    ].sort();

    const userGroups = {};

    data.forEach(({ fecha, UsuarioID, total_verificaciones, usuario_nombre }) => {
      let formattedFecha = "";
      if (typeof fecha === "string") {
        formattedFecha = fecha.split("T")[0];
      } else if (typeof fecha === "number") {
        formattedFecha = moment()
          .week(fecha % 100)
          .year(Math.floor(fecha / 100))
          .format("YYYY-Www");
      }

      const colorIndex = UsuarioID % 12;
      const colorPalette = [
        "#ff4d4d",
        "#18dcff",
        "#cd84f1",
        "#ff4757",
        "#b33939",
        "#ff793f",
        "#7158e2",
        "#706fd3",
        "#ffaf40",
        "#3ae374",
        "#ffcccc",
        "#fff200",
      ];

      const userColor = colorPalette[colorIndex];

      if (!userGroups[UsuarioID]) {
        userGroups[UsuarioID] = {
          label: usuario_nombre ? `${usuario_nombre}` : `User ${UsuarioID}`,
          data: Array(labels.length).fill(0),
          borderWidth: 2,
          borderColor: userColor,
        };
      }

      const index = labels.indexOf(formattedFecha);
      if (index !== -1) userGroups[UsuarioID].data[index] = total_verificaciones ?? 0;
    });

    return { labels, datasets: Object.values(userGroups) };
  };

  const showDrawer = async (title, endpoint) => {
    let columns = [];
    if (endpoint === "totalcount" || endpoint === "countsinpapeleria" || endpoint === "countsinpago") {
      columns = [
        { title: "No.", dataIndex: "rowNumber", key: "rowNumber" },
        { title: "Control Interno", dataIndex: "ControlInterno", key: "ControlInterno" },
        { title: "Status", dataIndex: "Status", key: "Status", render: (status) => getStatusTag(status) },
      ];
    } else if (endpoint === "totalsum") {
      columns = [
        { title: "No.", dataIndex: "rowNumber", key: "rowNumber" },
        { title: "Control Interno", dataIndex: "ControlInterno", key: "ControlInterno" },
        { title: "Monto", dataIndex: "Monto", key: "Monto" },
      ];
    }

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/estadisticas/${endpoint}list`, {
        params: getApiParams(startDate, endDate),
      });
      setListData(res.data);
      setDrawerTitle(title);
      setDrawerColumns(columns);
      setDrawerVisible(true);
    } catch (err) {
      console.error(`Error fetching ${endpoint}:`, err);
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  // Stats card configuration
  const countStats = [
    {
      title: "Total Verificaciones",
      value: totalCount?.total,
      icon: <FileText size={18} strokeWidth={1.5} style={{ color: "#1890ff", verticalAlign: "middle" }} />,
      endpoint: "totalcount",
      color: "#1890ff",
    },
    ...(auth.Rol === "administrador"
      ? []
      : [
          {
            title: "Sin Papelería",
            value: sinPapeleria?.total,
            icon: <Clock size={18} strokeWidth={1.5} style={{ color: "#faad14", verticalAlign: "middle" }} />,
            endpoint: "countsinpapeleria",
            color: "#faad14",
          },
        ]),
    {
      title: "Sin Pago",
      value: sinPago?.total,
      icon: <Clock size={18} strokeWidth={1.5} style={{ color: "#ff4d4f", verticalAlign: "middle" }} />,
      endpoint: "countsinpago",
      color: "#ff4d4f",
    },
  ];

  const montoStats = [
    {
      title: "Suma Total",
      value: totalMonto?.total?.toLocaleString(),
      icon: <DollarSign size={18} strokeWidth={1.5} style={{ color: "#52c41a", verticalAlign: "middle" }} />,
      endpoint: "totalsum",
      color: "#52c41a",
      prefix: "$",
    },
    {
      title: "Suma Unidad",
      value: totalMontoUnidad?.total?.toLocaleString(),
      icon: <DollarSign size={18} strokeWidth={1.5} style={{ color: "#1890ff", verticalAlign: "middle" }} />,
      endpoint: "totalsumunidad",
      color: "#1890ff",
      prefix: "$",
    },
    {
      title: "Suma Verificador",
      value: totalMontoVerificador?.total?.toLocaleString(),
      icon: <DollarSign size={18} strokeWidth={1.5} style={{ color: "#722ed1", verticalAlign: "middle" }} />,
      endpoint: "totalsumverificador",
      color: "#722ed1",
      prefix: "$",
    },
  ];

  return (
    <div style={{ minHeight: "100vh" }}>
      <PageWrapper className="dashboard-main-card">
        <PageTitle icon={<BarChart2 />} title="Dashboard de Estadísticas" />

        {/* Filter Section */}
        <div
          style={{
            padding: "16px",
            background: "rgba(249, 250, 251, 0.8)",
            borderRadius: "12px",
            border: "1px solid rgba(229, 231, 235, 0.6)",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
            marginBottom: "24px",
          }}
        >
          <TimeRangeSelector onUpdate={handleTimeRangeUpdate} startDate={startDate} endDate={endDate} groupBy={groupBy} selectedRange={selectedRange} />
        </div>

        {/* Count Statistics Section */}
        <div style={{ marginBottom: "24px" }}>
          <Title level={4} style={{ marginBottom: "16px", color: "#595959" }}>
            <CheckCircle size={18} strokeWidth={1.5} style={{ marginRight: "8px", verticalAlign: "middle" }} />
            Estadísticas de Verificaciones
          </Title>
          <Row gutter={[16, 16]}>
            {countStats.map(
              (stat, index) =>
                stat.value !== undefined && (
                  <Col key={index} xs={24} sm={12} md={8} lg={6}>
                    <Card
                      hoverable
                      style={{
                        borderRadius: "12px",
                        border: `1px solid ${stat.color}20`,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        transition: "all 0.3s ease",
                      }}
                      bodyStyle={{ padding: "20px" }}
                    >
                      <Statistic
                        title={
                          <Space>
                            {stat.icon}
                            <Text style={{ fontWeight: 500, color: "#595959" }}>{stat.title}</Text>
                          </Space>
                        }
                        value={stat.value}
                        valueStyle={{
                          color: stat.color,
                          fontSize: "28px",
                          fontWeight: "bold",
                        }}
                      />
                      <Button
                        type="link"
                        icon={<Eye size={18} strokeWidth={1.5} />}
                        onClick={() => showDrawer(stat.title, stat.endpoint)}
                        style={{
                          padding: "4px 0",
                          height: "auto",
                          marginTop: "8px",
                          color: stat.color,
                        }}
                      >
                        Ver Detalles
                      </Button>
                    </Card>
                  </Col>
                )
            )}
          </Row>
        </div>

        {/* Amount Statistics Section */}
        <div style={{ marginBottom: "24px" }}>
          <Title level={4} style={{ marginBottom: "16px", color: "#595959" }}>
            <DollarSign size={18} strokeWidth={1.5} style={{ marginRight: "8px", verticalAlign: "middle" }} />
            Estadísticas Monetarias
          </Title>
          <Row gutter={[16, 16]}>
            {montoStats.map(
              (stat, index) =>
                stat.value !== undefined && (
                  <Col key={index} xs={24} sm={12} md={8} lg={6}>
                    <Card
                      hoverable
                      style={{
                        borderRadius: "12px",
                        border: `1px solid ${stat.color}20`,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        transition: "all 0.3s ease",
                      }}
                      bodyStyle={{ padding: "20px" }}
                    >
                      <Statistic
                        title={
                          <Space>
                            {stat.icon}
                            <Text style={{ fontWeight: 500, color: "#595959" }}>{stat.title}</Text>
                          </Space>
                        }
                        value={stat.value}
                        prefix={stat.prefix}
                        valueStyle={{
                          color: stat.color,
                          fontSize: "24px",
                          fontWeight: "bold",
                        }}
                      />
                      <Button
                        type="link"
                        icon={<Eye size={18} strokeWidth={1.5} />}
                        onClick={() => showDrawer(stat.title, stat.endpoint)}
                        style={{
                          padding: "4px 0",
                          height: "auto",
                          marginTop: "8px",
                          color: stat.color,
                        }}
                      >
                        Ver Detalles
                      </Button>
                    </Card>
                  </Col>
                )
            )}
          </Row>
        </div>

        {/* User Charts Section - Only for Directors */}
        {(auth?.Rol === "director" || auth?.Rol === "administrador") && (
          <Card
            title={
              <Title level={4} style={{ margin: 0, color: "#595959" }}>
                <TrendingUp size={18} strokeWidth={1.5} style={{ marginRight: "8px", verticalAlign: "middle" }} />
                Análisis por Usuario
              </Title>
            }
            bordered={false}
            style={{
              borderRadius: "12px",
              marginBottom: "24px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
            bodyStyle={{ padding: "24px" }}
          >
            <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
              <Col xs={24} lg={12}>
                <BarChart
                  title="Total Acumulado"
                  labels={montoTotalTotal?.labels || []}
                  datasets={montoTotalTotal?.datasets || []}
                  rawData={montoTotalTotal?.rawData || []}
                />
              </Col>
              <Col xs={24} lg={12}>
                <BarChart
                  title="Este Año"
                  labels={montoTotalAnual?.labels || []}
                  datasets={montoTotalAnual?.datasets || []}
                  rawData={montoTotalAnual?.rawData || []}
                />
              </Col>
              <Col xs={24} lg={12}>
                <BarChart
                  title="Este Mes"
                  labels={montoTotalMensual?.labels || []}
                  datasets={montoTotalMensual?.datasets || []}
                  rawData={montoTotalMensual?.rawData || []}
                />
              </Col>
              <Col xs={24} lg={12}>
                <BarChart
                  title="Periodo Seleccionado"
                  labels={montoTotalPeriodo?.labels || []}
                  datasets={montoTotalPeriodo?.datasets || []}
                  rawData={montoTotalPeriodo?.rawData || []}
                />
              </Col>
            </Row>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
              <TimeRangeSelector onUpdate={handleTimeRangeUpdate} startDate={startDate} endDate={endDate} groupBy={groupBy} selectedRange={selectedRange} />
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <LineChart title="Monto Por Usuario" labels={montoData?.labels || []} datasets={montoData?.datasets || []} />
              </Col>
            </Row>
          </Card>
        )}

        {/* Status and Cost Analysis Section */}
        <Card
          title={
            <Title level={4} style={{ margin: 0, color: "#595959" }}>
              <PieChart size={18} strokeWidth={1.5} style={{ marginRight: "8px", verticalAlign: "middle" }} />
              Análisis de Estado y Costos
            </Title>
          }
          bordered={false}
          style={{
            borderRadius: "12px",
            marginBottom: "24px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          bodyStyle={{ padding: "24px" }}
        >
          <Row gutter={[16, 16]}>
            {statusCounts && (
              <Col xs={24} md={12} lg={8}>
                <Card
                  title={
                    <Space>
                      <PieChart size={18} strokeWidth={1.5} style={{ color: "#722ed1", verticalAlign: "middle" }} />
                      <Text strong>Distribución por Estado</Text>
                    </Space>
                  }
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    height: "100%",
                  }}
                  bodyStyle={{ padding: "20px" }}
                >
                  <DonaProgreso
                    labels={statusLabels}
                    data={[
                      statusCounts.en_proceso,
                      statusCounts.en_revision,
                      statusCounts.emitidas,
                      statusCounts.pagadas,
                      statusCounts.canceladas,
                    ]}
                    colors={colors}
                  />
                </Card>
              </Col>
            )}
            <Col xs={24} md={12} lg={16}>
              <Card
                title={
                  <Space>
                    <TrendingUp size={18} strokeWidth={1.5} style={{ color: "#52c41a", verticalAlign: "middle" }} />
                    <Text strong>Evolución de Montos</Text>
                  </Space>
                }
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  height: "100%",
                }}
                bodyStyle={{ padding: "20px" }}
              >
                {costData ? (
                  <AreaCostos
                    title="Monto de Verificaciones"
                    labels={costData.map((item) => item.fecha)}
                    data={costData.map((item) => item.total_monto)}
                    colors={["#52c41a"]}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                    }}
                  >
                    <Text type="secondary">Cargando datos...</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Card>

        <ListDrawer
          visible={drawerVisible}
          onClose={closeDrawer}
          data={listData}
          title={drawerTitle}
          columns={drawerColumns}
          key={drawerTitle}
        />
      </PageWrapper>
    </div>
  );
}
